import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import SEO, { getServiceSchema, getBreadcrumbSchema } from '../../components/SEO';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VIDEO & BRAND CONTENT PRICING PAGE - /pricing/video-brand
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PricingVideoBrand = () => {
  const location = useLocation();

  // Handle hash scroll on page load
  useEffect(() => {
    if (location.hash) {
      const scrollToHash = () => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      
      if (document.readyState === 'complete') {
        setTimeout(scrollToHash, 100);
      } else {
        window.addEventListener('load', () => setTimeout(scrollToHash, 100), { once: true });
      }
      
      const backupTimer = setTimeout(scrollToHash, 800);
      return () => clearTimeout(backupTimer);
    }
  }, [location.hash]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title="Video Production & Brand Content Pricing | Arizona"
        description="Professional video production for brands and businesses. Brand videos, short-form content, marketing reels & social media videos. Serving Arizona & nationwide."
        path="/pricing/video-brand"
        jsonLd={[
          getServiceSchema({
            name: 'Video Production & Brand Content',
            description: 'Professional video production services including brand videos, short-form content, marketing reels, and vertical social videos.',
            url: 'https://rcstudios.net/pricing/video-brand',
            areaServed: 'United States',
            priceRange: '$149-$1,500+'
          }),
          getBreadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Pricing', path: '/pricing' },
            { name: 'Video & Brand', path: '/pricing/video-brand' }
          ])
        ]}
      />

      {/* Hero Section */}
      <section className="bg-[#050816] text-white pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-[1000px]">
          {/* Back Link */}
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm font-medium mb-8 transition-colors"
            data-testid="back-to-categories"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Categories
          </Link>
          
          <div className="text-center">
            <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-4">Video & Brand Content</p>
            <h1 className="font-serif font-semibold text-4xl md:text-5xl mb-6">
              Video & Brand Content Services
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Compelling video content that tells your story and connects with your audience.
            </p>
          </div>
        </div>
      </section>

      {/* Brand Videos Section */}
      <section id="brand-videos" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-video">
              <img
                src="https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&q=80&w=800"
                alt="Brand Video Production"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white">
                <p className="text-[#F5C056] text-xs font-bold uppercase tracking-widest mb-2">Professional Production</p>
                <h3 className="font-serif text-2xl font-bold">Brand Videos</h3>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Brand Videos</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                High-quality video content that showcases your brand's personality, values, and story. 
                From concept to final edit, we handle everything.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$1,500</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "Full-day production",
                    "Professional crew",
                    "Script development assistance",
                    "2-3 minute final edit",
                    "Licensed music included",
                    "Multiple format exports"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/contact">Request Consultation</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Short-Form Content Section */}
      <section id="short-form" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Short-Form Content</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Bite-sized video content optimized for social media engagement.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-[900px] mx-auto">
            {[
              { title: 'Single Video', price: '$199', features: ['15-60 second video', 'Basic editing', 'One revision', 'Vertical format'] },
              { title: 'Content Bundle', price: '$499', features: ['3 videos', 'Custom graphics', 'Music licensing', 'Two revisions each'] },
              { title: 'Monthly Package', price: '$1,200', features: ['8 videos/month', 'Content strategy', 'Trend research', 'Priority scheduling'] },
            ].map((pkg, i) => (
              <div key={i} className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-all">
                <h3 className="font-serif text-xl text-gray-900 mb-2">{pkg.title}</h3>
                <p className="text-2xl font-serif font-medium text-gray-900 mb-4">{pkg.price}</p>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-1.5"></div>
                      {f}
                    </li>
                  ))}
                </ul>
                <Button asChild variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg">
                  <Link to="/contact">Request a Quote</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketing Reels Section */}
      <section id="reels" className="py-20 bg-[#FAF9F6] scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 lg:order-1">
              <h2 className="font-serif text-3xl md:text-4xl text-gray-900">Marketing Reels</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Dynamic video reels designed to capture attention and drive engagement on Instagram, TikTok, and YouTube Shorts.
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-4">
                  <p className="text-sm text-muted-foreground">Starting at</p>
                  <p className="text-3xl font-serif font-medium text-gray-900">$299</p>
                </div>
                
                <div className="space-y-3 mb-6">
                  {[
                    "15-90 second professional reel",
                    "Trending audio selection",
                    "Motion graphics & text overlays",
                    "Optimized for algorithm performance",
                    "Platform-specific exports"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                      <span className="text-gray-600 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
                  <Link to="/contact">Request a Quote</Link>
                </Button>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-[9/16] max-w-[300px] mx-auto order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&q=80&w=400"
                alt="Marketing Reels"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Vertical Social Videos Section */}
      <section id="vertical" className="py-20 bg-white scroll-mt-24">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-4">Vertical Social Videos</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Optimized vertical video content designed specifically for Instagram Stories, TikTok, and mobile-first platforms.
            </p>
          </div>

          <div className="bg-[#FAF9F6] rounded-xl p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground">Starting at</p>
              <p className="text-4xl font-serif font-medium text-gray-900">$149</p>
              <p className="text-sm text-muted-foreground mt-1">per video</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                "9:16 vertical format",
                "Attention-grabbing hooks",
                "Captions included",
                "Trending effects",
                "Quick turnaround",
                "Bulk discounts available"
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2"></div>
                  <span className="text-gray-600 text-sm">{item}</span>
                </div>
              ))}
            </div>

            <Button asChild className="w-full bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-11">
              <Link to="/contact">Request a Quote</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Back to Categories Footer */}
      <section className="py-12 bg-[#FAF9F6] border-t border-gray-200">
        <div className="container mx-auto px-6 text-center">
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-[#050816] hover:text-[#F5C056] font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Categories
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PricingVideoBrand;
